import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedTableName } from "../../store/slices/selectedTableSlice";
import DashboardContent from "../../components/DashboardContent";
import { CsrAPI } from "../../apis/CsrAPI";
import Loader from "../../utils/Loader";
import { AgGridReact } from "ag-grid-react";
import { CFormInput } from "@coreui/react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ReactComponent as ViewIcon } from "../../assets/images/icons/view.svg";
import { ReactComponent as GenerateIcon } from "../../assets/images/icons/generate.svg";

import {
  ClientSideRowModelModule,
  ModuleRegistry,
  TooltipModule,
  ValidationModule,
} from "ag-grid-community";
import { RowGroupingModule } from "ag-grid-enterprise";
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowGroupingModule,
  TooltipModule,
  ...(process.env.NODE_ENV !== "production" ? [ValidationModule] : []),
]);

const MIN_ROW_HEIGHT = 56;
const FILTER_VIEW_ALL = "all";
const FILTER_FAILED = "failed";
const FILTER_PROCESSED = "processed";

// Estimate wrapped lines for row height when expanded (~50 chars/line conservative for narrow columns)
const estimateWrappedLines = text =>
  text ? Math.max(1, Math.ceil(text.length / 50)) : 1;

const getExpandedRowHeight = tableTitle => {
  const lines = estimateWrappedLines(tableTitle);
  const lineHeight = 18;
  return MIN_ROW_HEIGHT + (lines - 1) * lineHeight;
};

const TableNameCell = ({ value, tableId, isExpanded, onToggle }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el || !value || isExpanded) return;

    const checkTruncation = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    checkTruncation();
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el.parentElement);

    return () => observer.disconnect();
  }, [value, isExpanded]);

  const showExpandButton = value && (isTruncated || isExpanded);

  return (
    <div className="advanced-search__table-name-cell">
      <span
        ref={textRef}
        className={`advanced-search__table-name-text ${isExpanded ? "advanced-search__table-name-text--wrap" : "advanced-search__table-name-text--truncate"}`}>
        {value || ""}
      </span>
      {showExpandButton && (
        <button
          type="button"
          className="advanced-search__table-name-toggle"
          onClick={e => {
            e.stopPropagation();
            onToggle(tableId);
          }}
          aria-label={isExpanded ? "Collapse" : "Expand"}>
          <span
            className={`advanced-search__caret ${isExpanded ? "advanced-search__caret--up" : "advanced-search__caret--down"}`}
          />
        </button>
      )}
    </div>
  );
};

function flattenDashboardBody(data) {
  if (!data || typeof data !== "object") {
    return [];
  }

  const sourceBody = data;
  if (!sourceBody || typeof sourceBody !== "object") {
    return [];
  }

  const flattened = [];

  // Iterate all top-level sections in body (e.g., Efficacy, Safety, etc.)
  for (const [sectionName, subsections] of Object.entries(sourceBody)) {
    if (!Array.isArray(subsections)) continue;

    for (const subsectionObj of subsections) {
      // Safely extract subsection name and tables
      const subsectionName = subsectionObj?.subsection ?? null;
      const tables = Array.isArray(subsectionObj?.tables)
        ? subsectionObj.tables
        : [];

      for (let i = 0; i < tables.length; i++) {
        const tbl = tables[i];
        if (!tbl || typeof tbl !== "object") continue;

        // Use a unique row key for ag-grid (tableId can be duplicated in source data)
        const rowKey = `${sectionName}|${subsectionName}|${tbl.tableId ?? ""}|${i}`;

        // Copy table properties and add section/subsection
        flattened.push({
          _rowKey: rowKey,
          tableId: tbl.tableId ?? null,
          tableTitle: tbl.tableTitle ?? null,
          ruleStatus:
            typeof tbl.ruleStatus === "boolean" ? tbl.ruleStatus : null,
          aiStatus: tbl.aiStatus ?? null,
          section: sectionName,
          subsection: subsectionName,
        });
      }
    }
  }

  return flattened;
}

const AdvancedSearch = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState(FILTER_VIEW_ALL);
  const [searchQuery, setSearchQuery] = useState("");
  const [groupsExpanded, setGroupsExpanded] = useState(false);
  const [expandedTableIds, setExpandedTableIds] = useState(new Set());

  const filteredRowData = useMemo(() => {
    return rowData.filter(row => {
      const matchesFilter =
        filterStatus === FILTER_VIEW_ALL
          ? true
          : filterStatus === FILTER_FAILED
            ? row.ruleStatus === false
            : row.ruleStatus === true;
      const matchesSearch =
        !searchQuery ||
        (row.tableTitle || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [rowData, filterStatus, searchQuery]);

  const itemCount = filteredRowData.length;

  const handleExpandCollapseAll = useCallback(() => {
    if (!gridRef.current?.api) return;
    if (groupsExpanded) {
      gridRef.current.api.collapseAll();
    } else {
      gridRef.current.api.expandAll();
    }
    setGroupsExpanded(prev => !prev);
  }, [groupsExpanded]);

  const handleTableNameToggle = useCallback(tableId => {
    setExpandedTableIds(prev => {
      const next = new Set(prev);
      if (next.has(tableId)) next.delete(tableId);
      else next.add(tableId);
      return next;
    });
    setTimeout(() => gridRef.current?.api?.resetRowHeights(), 0);
  }, []);

  const getRowHeight = useCallback(
    params => {
      if (params.node?.group) return MIN_ROW_HEIGHT;
      const data = params.data;
      if (!data?.tableId || !data?.tableTitle) return MIN_ROW_HEIGHT;
      const isExpanded = expandedTableIds.has(data.tableId);
      if (isExpanded) {
        return Math.max(MIN_ROW_HEIGHT, getExpandedRowHeight(data.tableTitle));
      }
      return MIN_ROW_HEIGHT;
    },
    [expandedTableIds]
  );

  const columnDefs = useMemo(
    () => [
      { field: "section", rowGroup: true, hide: true },
      { field: "subsection", rowGroup: true, hide: true },
      {
        field: "tableTitle",
        headerName: "Table name",
        flex: 1,
        minWidth: 200,
        // tooltipValueGetter: params => params.value || null,
        cellRenderer: params => {
          if (params.node?.group) return null;
          return (
            <TableNameCell
              value={params.value}
              tableId={params.data?.tableId}
              isExpanded={expandedTableIds.has(params.data?.tableId)}
              onToggle={handleTableNameToggle}
            />
          );
        },
      },
      {
        field: "ruleStatus",
        headerName: "Status",
        colId: "status",
        width: 110,
        cellClass: "advanced-search__status-cell",
        headerClass: "advanced-search__status-cell",
        cellRenderer: params => {
          if (!params.data || params.node?.group) return null;
          const rs = params.data.ruleStatus;
          const status =
            rs === true ? "Successful" : rs === false ? "Failed" : "Processing";
          return (
            <span
              className={`advanced-search__status-pill advanced-search__status-pill--${status.toLowerCase()}`}>
              {status}
            </span>
          );
        },
      },
      {
        field: "aiStatus",
        headerName: "Advanced Search",
        width: 150,
        sortable: false,
        filter: false,
        cellRenderer: params => {
          if (!params.data || params.node?.group) return null;
          const aiStatus = params.data.aiStatus;
          const isReady = aiStatus === "QUEUED" || aiStatus === "ready";
          const isProcessing =
            aiStatus === "PROCESSING" || aiStatus === "in_progress";
          const isError = aiStatus === "ready_with_errors";
          const pillVariant = isError
            ? "error"
            : isReady
              ? "ready"
              : isProcessing
                ? "processing"
                : "not-generated";
          const statusLabel = isError
            ? "Error"
            : isReady
              ? "Ready"
              : isProcessing
                ? "Processing"
                : "Not generated";

          return (
            <div className="advanced-search__triloai-cell">
              <span
                className={`advanced-search__triloai-pill advanced-search__triloai-pill--${pillVariant}`}>
                {statusLabel}
              </span>
            </div>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        colId: "actions",
        width: 90,
        sortable: false,
        filter: false,
        tooltipValueGetter: params => {
          if (!params.data || params.node?.group) return null;
          const aiStatus = params.data.aiStatus;
          const isProcessing =
            aiStatus === "PROCESSING" || aiStatus === "IN_PROGRESS";
          if (isProcessing) return null;
          return aiStatus === "PROCESSING" ||
            aiStatus === "IN_PROGRESS" ||
            aiStatus === "ready" ||
            aiStatus === "ready_with_errors"
            ? "View AI response"
            : "Generate AI response";
        },
        cellRenderer: params => {
          if (!params.data || params.node?.group) return null;
          const aiStatus = params.data.aiStatus;
          const isReady =
            aiStatus === "QUEUED" ||
            aiStatus === "ready" ||
            aiStatus === "ready_with_errors";
          const isProcessing =
            aiStatus === "PROCESSING" || aiStatus === "IN_PROGRESS";
          const tableId = params.data.tableId;
          const tableTitle = params.data.tableTitle ?? "";

          const handleActionClick = () => {
            if (!studyId || !tableId || isProcessing) return;
            dispatch(setSelectedTableName(tableTitle));
            if (isReady) {
              navigate(
                `/csr/${studyId}/table-analysis-ai-response/${tableId}`,
                {
                  state: {
                    tableName: tableTitle,
                  },
                }
              );
            } else {
              navigate(`/csr/${studyId}/table-analysis-questions/${tableId}`, {
                state: {
                  tableName: tableTitle,
                  section: params.data?.section ?? "",
                  subsection: params.data?.subsection ?? "",
                },
              });
            }
          };

          if (isProcessing) return null;

          return (
            <div className="advanced-search__actions-cell">
              <span
                className="as-actions"
                onClick={e => {
                  e.stopPropagation();
                  handleActionClick();
                }}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleActionClick();
                  }
                }}>
                {isReady ? <ViewIcon /> : <GenerateIcon />}
              </span>
            </div>
          );
        },
      },
    ],
    [studyId, navigate, dispatch, expandedTableIds, handleTableNameToggle]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  const onGridReady = useCallback(params => {
    params.api.collapseAll();
  }, []);

  useEffect(() => {
    if (!studyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    CsrAPI.getAdvSearchDashboard({ studyId })
      .then(data => {
        if (cancelled) return;
        setRowData(flattenDashboardBody(data ? data : {}));
      })
      .catch(err => {
        console.error("Error fetching dashboard data:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  const PageContent = () => {
    if (loading) {
      return (
        <div className="advanced-search advanced-search__wrapper advanced-search__loading">
          <Loader loading={true} />
        </div>
      );
    }
    if (error) {
      return (
        <div className="advanced-search advanced-search__wrapper advanced-search__error">
          <p className="advanced-search__error-message">{error}</p>
        </div>
      );
    }
    return (
      <div className="advanced-search advanced-search__wrapper">
        <div className="advanced-search__header">
          <h2 className="advanced-search__title">Your tables</h2>
          <p className="advanced-search__subtitle">
            Select a table from the list below to generate and edit AI
            responses.
          </p>
          <div className="advanced-search__controls">
            <div className="advanced-search__filters">
              <button
                type="button"
                className={`advanced-search__filter-btn ${filterStatus === FILTER_VIEW_ALL ? "advanced-search__filter-btn--active" : ""}`}
                onClick={() => setFilterStatus(FILTER_VIEW_ALL)}>
                View all
              </button>
              <button
                type="button"
                className={`advanced-search__filter-btn ${filterStatus === FILTER_FAILED ? "advanced-search__filter-btn--active" : ""}`}
                onClick={() => setFilterStatus(FILTER_FAILED)}>
                Failed to process
              </button>
              <button
                type="button"
                className={`advanced-search__filter-btn ${filterStatus === FILTER_PROCESSED ? "advanced-search__filter-btn--active" : ""}`}
                onClick={() => setFilterStatus(FILTER_PROCESSED)}>
                Processed successfully
              </button>
              <div className="advanced-search__search-wrap">
                <CFormInput
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="advanced-search__search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="advanced-search__search-clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search">
                    <span aria-hidden>×</span>
                  </button>
                )}
              </div>
            </div>
            <div className="advanced-search__footer-controls">
              <span className="advanced-search__item-count">
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </span>
              <button
                type="button"
                className="advanced-search__collapse-btn"
                onClick={handleExpandCollapseAll}
                title={
                  groupsExpanded ? "Collapse all groups" : "Expand all groups"
                }>
                <span
                  className={`advanced-search__collapse-caret ${groupsExpanded ? "advanced-search__collapse-caret--up" : "advanced-search__collapse-caret--down"}`}
                />
                {groupsExpanded ? "Collapse all" : "Expand all"}
              </button>
            </div>
          </div>
        </div>
        <div
          className="ag-theme-alpine advanced-search__grid"
          style={{ height: "600px", width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            groupDisplayType="groupRows"
            animateRows={true}
            headerHeight={44}
            getRowHeight={getRowHeight}
            tooltipShowDelay={0}
            onGridReady={onGridReady}
            getRowId={params =>
              params.data?._rowKey ?? params.rowIndex?.toString()
            }
          />
        </div>
      </div>
    );
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Advanced Search"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "CSR File",
        buttonGoToAction: "/csr/" + studyId + "/file",
      }}
      nextButton={false}
    />
  );
};

export default AdvancedSearch;
